package pl.zar.tikthrow.shared.dto

import kotlin.time.Instant
import kotlinx.serialization.Serializable
import pl.zar.tikthrow.shared.domain.ApiCredential
import pl.zar.tikthrow.shared.domain.User

/**
 * `POST /api/v1/keys` request body.
 *
 * @property scopes optional scope wire names (`drafts:write`, ...); null = the
 * full default set. Unknown names are rejected with 400.
 * @property expiresInSeconds optional lifetime; null = the token never expires
 * (revocation still applies). Must be positive when present.
 * @property requireReview the optional per-key review gate (T405 / spec C3 +
 * FR-014); null/absent = off — submissions by this key are system-approved
 * and proceed without a human step (publication through the API does not
 * require a human).
 */
@Serializable
data class CreateApiKeyRequest(
    val name: String,
    val scopes: List<String>? = null,
    val expiresInSeconds: Long? = null,
    val requireReview: Boolean? = null,
    /**
     * Optional per-key rate-limit override (card #24 / T107): requests per
     * minute for this key's bucket; null/absent = the default limit.
     */
    val rateLimitRpm: Int? = null
)

/**
 * Masked key metadata — what `GET /api/v1/keys` and `POST /api/v1/keys`
 * (alongside the raw key, exactly once) expose. The raw token and its hash
 * are never included (constitution principle IV).
 */
@Serializable
data class ApiKeyInfo(
    val id: String,
    val name: String,
    val scopes: List<String>,
    val createdAt: Instant,
    val expiresAt: Instant?,
    val lastUsedAt: Instant?,
    val revokedAt: Instant?,
    /** The per-key review gate (T405); true = submissions wait for a human. */
    val requireReview: Boolean = false,
    /**
     * The per-key rate-limit override (card #24 / T107): requests per minute
     * for this key's bucket; null = the default limit.
     */
    val rateLimitRpm: Int? = null,
    /**
     * The per-key rejection metric (card #24 / T107): how many of this key's
     * requests were rate-limited (429). Viewable by the administrator.
     */
    val rejectedCount: Long = 0
)

/** `POST /api/v1/keys` response — [key] is the raw token, shown exactly once. */
@Serializable
data class CreateApiKeyResponse(
    val key: String,
    val info: ApiKeyInfo
)

/**
 * `GET /api/v1/me` response.
 *
 * @property credential how the request authenticated (session = human app
 * flow, api_token = machine token).
 * @property token present only when [credential] is [ApiCredential.API_TOKEN].
 */
@Serializable
data class ApiMeResponse(
    val user: User,
    val credential: ApiCredential,
    val token: ApiTokenInfo? = null
)

@Serializable
data class ApiTokenInfo(
    val id: String,
    val name: String,
    val scopes: List<String>,
    /** The per-key review gate (T405); true = submissions wait for a human. */
    val requireReview: Boolean = false
)
